import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, FolderOpen } from "lucide-react";
import { getBlogPostsByCategory, getBlogCategories } from "../lib/supabase";
import Pagination from "./Pagination";

export default function CategoryList() {
  const { category } = useParams();
  const [posts, setPosts] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);

  useEffect(() => {
    async function fetchData() {
      if (!category) return;

      try {
        setLoading(true);
        // Fetch both categories and posts in parallel
        const [categoriesData, postsData] = await Promise.all([
          getBlogCategories(),
          getBlogPostsByCategory(category, currentPage),
        ]);

        setCategories(categoriesData);
        setPosts(postsData.posts);
        setTotalPosts(postsData.total);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load content");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [category, currentPage]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F98B3D]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <p className="text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="blog-title">
        Category: {posts[0]?.blog_categories?.name || category}
      </h1>

      {/* Category Navigation */}
      <div className="mb-8">
        <div className="flex flex-wrap gap-3">
          <Link
            to="/blog"
            className="inline-flex items-center px-4 py-2 rounded-full bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors duration-200"
          >
            <FolderOpen className="h-4 w-4 mr-2" />
            All Posts
          </Link>
          {categories.map((cat) => (
            <Link
              key={cat.id}
              to={`/blog/category/${cat.slug}`}
              className={`inline-flex items-center px-4 py-2 rounded-full ${
                cat.slug === category
                  ? "bg-[#F98B3D] text-white"
                  : "bg-gray-100 text-gray-800 hover:bg-gray-200"
              } transition-colors duration-200`}
            >
              <FolderOpen className="h-4 w-4 mr-2" />
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      {posts.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600">No posts found in this category.</p>
          <Link
            to="/blog"
            className="text-[#F98B3D] hover:text-[#e07a2c] mt-4 inline-block"
          >
            View all posts
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {posts.map((post) => (
            <article key={post.id} className="blog-card">
              {post.image_url && (
                <img
                  className="w-full h-48 object-cover"
                  src={post.image_url}
                  alt={post.title}
                />
              )}
              <div className="p-6">
                <div className="blog-meta mb-2">
                  <time
                    dateTime={post.published_at}
                    className="flex items-center"
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    {new Date(post.published_at).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </time>
                  <span>•</span>
                  <span className="flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {post.read_time} min read
                  </span>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">
                  <Link
                    to={`/blog/${post.slug}`}
                    className="hover:text-[#F98B3D]"
                  >
                    {post.title}
                  </Link>
                </h2>
                <p className="text-gray-600">{post.excerpt}</p>
                {post.blog_authors && (
                  <div className="flex items-center mt-4 pt-4 border-t border-gray-100">
                    {post.blog_authors.image_url && (
                      <img
                        className="h-8 w-8 rounded-full mr-2"
                        src={post.blog_authors.image_url}
                        alt={post.blog_authors.name}
                      />
                    )}
                    <span className="text-sm text-gray-600">
                      {post.blog_authors.name}
                    </span>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(totalPosts / 10)}
        onPageChange={setCurrentPage}
      />
    </div>
  );
}
